import { isValidObjectId } from "@/lib/utils/input-validator";
import {
  listTrash as listTrashApi,
  purgeTrash as purgeTrashApi,
  restoreTrash as restoreTrashApi,
  trashCount as trashCountApi,
} from "@/lib/api/clients/GuestTrashClient";
import {
  readSkipped,
  type GuestTrashCountResult,
  type GuestTrashPage,
  type GuestTrashPurgeRequest,
  type GuestTrashPurgeResult,
  type GuestTrashRestoreRequest,
  type GuestTrashRestoreResult,
} from "@/types/GuestTrash";

/**
 * Guest-trash handlers (W6 WP8). Unlike most handlers these THROW: every
 * caller is a react-query `queryFn` / `mutationFn` or a try block that wants
 * the status on the error (`err.status === 403` ⇒ the page's no-access state).
 */

export async function fetchTrashPage(eventId: string, page: number, size: number): Promise<GuestTrashPage> {
  if (!isValidObjectId(eventId)) throw new Error("validationError");
  const result = await listTrashApi(eventId, page, size);
  return {
    items: Array.isArray(result?.items) ? result.items : [],
    total: typeof result?.total === "number" ? result.total : 0,
  };
}

/**
 * The trash's row count and whether it is ON for the event (`enabled`), or
 * `null` when the answer cannot be had — a backend without the route (404
 * after a rollback), a refusal, a network blip. The sidebar hides the entry
 * on `null`; it never guesses. An older backend without `enabled` reads as
 * `enabled: null`, and the browser's learned flag keeps deciding (H1).
 */
export async function fetchTrashCount(eventId: string): Promise<GuestTrashCountResult | null> {
  if (!isValidObjectId(eventId)) return null;
  try {
    const result = await trashCountApi(eventId);
    if (typeof result?.count !== "number") return null;
    return { count: result.count, enabled: typeof result.enabled === "boolean" ? result.enabled : null };
  } catch {
    return null;
  }
}

export async function restoreFromTrash(
  eventId: string,
  body: GuestTrashRestoreRequest,
): Promise<GuestTrashRestoreResult> {
  if (!isValidObjectId(eventId)) throw new Error("validationError");
  const result = await restoreTrashApi(eventId, body);
  return {
    restored: typeof result?.restored === "number" ? result.restored : 0,
    skipped: readSkipped(result?.skipped),
    warnings: Array.isArray(result?.warnings) ? result.warnings : [],
  };
}

/**
 * The purge door's answer, so the caller counts what WAS purged (and names
 * the rows skipped as `busy`) instead of what it asked for (lens-3 F6). An
 * empty body (older backend) reads as nothing purged — never a throw.
 */
export async function purgeFromTrash(eventId: string, body: GuestTrashPurgeRequest): Promise<GuestTrashPurgeResult> {
  if (!isValidObjectId(eventId)) throw new Error("validationError");
  const raw = await purgeTrashApi(eventId, body);
  const result = raw && typeof raw === "object" ? (raw as Partial<GuestTrashPurgeResult>) : null;
  return {
    purged: typeof result?.purged === "number" ? result.purged : 0,
    queued: typeof result?.queued === "number" ? result.queued : 0,
    skipped: readSkipped(result?.skipped),
    receiptIds: Array.isArray(result?.receiptIds) ? result.receiptIds.filter((r): r is string => typeof r === "string") : [],
  };
}
